import { useState } from 'react'
import { Plus, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import { useCreatePoll } from '@/hooks/use-polls'
import { getApiErrorMessage } from '@/lib/api'
import type { VotingUnit } from '@riverpark/types'

const SELECT_CLS =
  'flex h-10 w-full rounded border border-input bg-card px-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20'

function defaultStart(): string {
  return new Date().toISOString().slice(0, 16)
}

function defaultEnd(): string {
  const d = new Date()
  d.setDate(d.getDate() + 7)
  return d.toISOString().slice(0, 16)
}

export function PollModal({ onClose }: { onClose: () => void }) {
  const create = useCreatePoll()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [votingUnit, setVotingUnit] = useState<VotingUnit>('one_plot_one_vote')
  const [startAt, setStartAt] = useState(defaultStart())
  const [endAt, setEndAt] = useState(defaultEnd())
  const [options, setOptions] = useState(['', ''])
  const [error, setError] = useState<string | null>(null)

  function updateOption(index: number, value: string) {
    setOptions((prev) => prev.map((o, i) => (i === index ? value : o)))
  }

  function addOption() {
    if (options.length >= 10) return
    setOptions((prev) => [...prev, ''])
  }

  function removeOption(index: number) {
    if (options.length <= 2) return
    setOptions((prev) => prev.filter((_, i) => i !== index))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    const cleanOptions = options.map((o) => o.trim()).filter(Boolean)
    if (cleanOptions.length < 2) {
      setError('Add at least 2 options')
      return
    }
    if (new Date(endAt) <= new Date(startAt)) {
      setError('End date must be after the start date')
      return
    }

    try {
      await create.mutateAsync({
        title,
        description: description.trim() || undefined,
        votingUnit,
        startAt: new Date(startAt).toISOString(),
        endAt: new Date(endAt).toISOString(),
        options: cleanOptions.map((label) => ({ label })),
      })
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to create poll'))
    }
  }

  return (
    <Modal open onClose={onClose} labelledBy="poll-modal-title" panelClassName="max-w-xl">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <h2 id="poll-modal-title" className="font-serif text-xl text-ink">
            Create poll
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>

      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <ModalBody className="space-y-4">
          <Input
            label="Question"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            minLength={5}
            maxLength={200}
            placeholder="Should the estate approve the borehole maintenance budget?"
          />
          <div className="space-y-1.5">
            <label className="font-sans text-sm font-medium text-ink">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full rounded border border-input bg-card px-3.5 py-2 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
              placeholder="Add context members should read before voting."
            />
          </div>

          <div className="space-y-1.5">
            <label className="font-sans text-sm font-medium text-ink">Voting unit</label>
            <select
              className={SELECT_CLS}
              value={votingUnit}
              onChange={(e) => setVotingUnit(e.target.value as VotingUnit)}
            >
              <option value="one_plot_one_vote">One plot, one vote</option>
              <option value="one_member_one_vote">One member, one vote</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Opens"
              type="datetime-local"
              value={startAt}
              onChange={(e) => setStartAt(e.target.value)}
              required
            />
            <Input
              label="Closes"
              type="datetime-local"
              value={endAt}
              onChange={(e) => setEndAt(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <label className="font-sans text-sm font-medium text-ink">Options</label>
            <div className="space-y-2">
              {options.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={option}
                    onChange={(e) => updateOption(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    maxLength={100}
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => removeOption(index)}
                    disabled={options.length <= 2}
                    className="shrink-0 rounded p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:pointer-events-none disabled:opacity-30"
                    aria-label={`Remove option ${index + 1}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            {options.length < 10 && (
              <button
                type="button"
                onClick={addOption}
                className="inline-flex items-center gap-1.5 font-sans text-sm text-primary hover:text-green-lt"
              >
                <Plus size={14} />
                Add option
              </button>
            )}
          </div>

          {error && (
            <p className="font-sans text-xs text-destructive" role="alert">
              {error}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Create poll
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
